import {
  type UserTourState,
  type UserTourStateActions,
} from "@/features/tour/reducer";
import { JoyrideStyles } from "@/features/tour/styles";
import { setCompletedTour } from "@/features/tour/utils";
import { Button } from "@repo/skapa";
import { t } from "@repo/translation";
import { ACTIONS, EVENTS, type EventData, Joyride } from "react-joyride";

import { getProjectTourSteps } from "./utils";

export const ProjectOverviewTour = ({
  state,
  actions,
}: {
  state: UserTourState;
  actions: UserTourStateActions;
}) => {
  const projectTourSteps = getProjectTourSteps();

  const handleJoyrideCallback = (data: EventData) => {
    const { action, index, type } = data;
    const isCloseAction = action === ACTIONS.CLOSE;
    const isInitAction = action === ACTIONS.INIT;
    const isPrevAction = action === ACTIONS.PREV && type === EVENTS.STEP_AFTER;
    const isNextAction = action === ACTIONS.NEXT && type === EVENTS.STEP_AFTER;
    const isTargetNotFound =
      action === ACTIONS.NEXT && type === EVENTS.TARGET_NOT_FOUND;
    const isTourEnd = type === EVENTS.TOUR_END;

    if (isCloseAction) {
      actions.setIsRunning(false);
      actions.setStep(projectTourSteps.length - 1);
    }

    if (isInitAction) {
      actions.setIsRunning(true);
    }

    if (isPrevAction) {
      actions.setStep(index - 1);
    }

    if (isNextAction) {
      if (index === 3) {
        actions.projectOverviewOpenModal(true);
        setTimeout(() => {
          const element = document.querySelector(projectTourSteps[4]?.target);
          element?.scrollIntoView({ block: "center" });
          actions.setStep(index + 1);
        }, 500);
      } else {
        actions.setStep(index + 1);
      }
    }

    if (isTargetNotFound) {
      actions.setStep(index + 1);
    }

    if (isTourEnd) {
      actions.projectOverviewOpenModal(false);
      actions.setIsRunning(false);
      setCompletedTour(state.currentTour);
    }
  };

  const shouldDisableScrolling = (stepIndex: number) => {
    return stepIndex === 8 || stepIndex === 9 || stepIndex === 10;
  };

  const stepsWithScrollBehavior = projectTourSteps.map((step, stepIndex) => ({
    ...step,
    skipScroll: shouldDisableScrolling(stepIndex),
  }));

  return (
    <div data-testid="project-tour">
      <Joyride
        continuous
        stepIndex={state.currentStep}
        steps={stepsWithScrollBehavior}
        onEvent={handleJoyrideCallback}
        options={JoyrideStyles}
        locale={{
          back: <Button size="small">{t("label.back")}</Button>,
          last: (
            <Button size="small" type="primary">
              {t("label.end")}
            </Button>
          ),
          next: (
            <Button size="small" type="primary">
              {t("label.next")}
            </Button>
          ),
        }}
      />
    </div>
  );
};
